import { useToast as internalUseToast } from '@nuxt/ui/composables'
import type { FAIconName } from '#shared/types/generated-icons'
import type { Toast } from '#ui/composables/useToast'
import { generateRandomString } from '~/utils/helpers'

type ToastType = 'info' | 'warning' | 'success' | 'error' | 'add'

type ToastParam = string | Partial<Toast>
type ToastProxy = {
  [K in ToastType]: (message: ToastParam) => void
}

const icons: Record<Exclude<ToastType, 'add'>, FAIconName> = {
  info: 'fa-info-circle',
  success: 'fa-check-circle',
  warning: 'fa-exclamation-triangle',
  error: 'fa-exclamation-triangle'
}

type ToastProxyReturn = {
  toast: ToastProxy
} & {
  [K in 'info' | 'warning' | 'success' | 'error']: (message: ToastParam) => void
}

export function addToast(id?: string): ToastProxyReturn {
  const nuxtAppAvailable = tryUseNuxtApp()

  if (!nuxtAppAvailable) {
    const toast = new Proxy<ToastProxy>({} as any, {
      get(_target: unknown, _m: string): any {
        return () => {}
      }
    })

    return {
      toast,
      info: toast.info,
      success: toast.success,
      warning: toast.warning,
      error: toast.error
    }
  }

  const toaster = internalUseToast()
  const { polite } = useAnnouncer()

  id ??= generateRandomString(5)

  const toast = new Proxy<ToastProxy>({} as any, {
    get(_target: unknown, m: string): any {
      if (!['info', 'success', 'warning', 'error', 'add'].includes(m)) {
        throw new Error(`Unsupported Toast method: ${m}`)
      }

      const method = m as ToastType

      return (message: ToastParam) => {
        let ui: Toast['ui'] = {
          root: tw`bg-accented`,
          close: tw`cursor-pointer`
        }
        if (typeof message !== 'string' && message.ui) {
          ui = nuxtUiMerge(message.ui, ui)
        }

        if (method === 'add') {
          if (typeof message === 'string') {
            throw new Error('Cannot add a string to the toast stack')
          } else {
            toaster.add({
              ...message,
              ui,
              id
            })

            if (typeof message.title === 'string') {
              polite(message.title)
            }

            return
          }
        }

        if (typeof message === 'string') {
          toaster.add({
            title: message,
            color: method,
            icon: icons[method],
            ui,
            id
          })
          polite(message)
        } else {
          toaster.add({
            ...message,
            color: method,
            icon: icons[method],
            ui,
            id
          })

          if (typeof message.title === 'string') {
            polite(message.title)
          }
        }
      }
    }
  })

  return {
    toast,
    info: toast.info,
    success: toast.success,
    warning: toast.warning,
    error: toast.error
  }
}
